import { geocodeByZipCode } from '@/data/Content/GeocodeByZipCode';
import { EditableAddress } from '@/data/types/Address';
import { KeyboardEvent } from 'react';
import { isNoExteriorNumber } from 'shared/custom/utils/exteriorNumber';
import { OTHER_COLONY } from '@/components/blocks/AddressForm/constants';

const controlKeys = new Set([
	'Backspace',
	'Delete',
	'ArrowLeft',
	'ArrowRight',
	'ArrowUp',
	'ArrowDown',
	'Tab',
	'Home',
	'End',
	'Escape',
	'Enter',
]);

const ctrlShortcuts = new Set(['a', 'c', 'v', 'x', 'z', 'y']);

export const toNormalForm = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const capitalizeFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const toStr = (v?: string | null) => (v ?? '').trim();
export const isNonEmpty = (v?: string | null) => toStr(v).length > 0;
export const notSelectOption = (v?: string | null) => toStr(v) !== '0';

export const validateNumericInput = (e: KeyboardEvent<HTMLDivElement>) => {
	if ((e.ctrlKey || e.metaKey) && ctrlShortcuts.has(e.key.toLowerCase())) return;

	if (controlKeys.has(e.key)) return;

	if (!/^\d$/.test(e.key)) {
		e.preventDefault();
	}
};

export const getLatLng = (results: google.maps.GeocoderResult[] | null) => {
	const loc = results?.[0]?.geometry.location;
	if (!loc) return undefined;

	return `${loc.lat()},${loc.lng()}`;
};

const isSameCountryAddress = (
	results: google.maps.GeocoderResult[] | null,
	country: string
): boolean => {
	const first = results?.[0];

	return (
		first?.address_components?.some(
			(c) => c.types.includes('country') && c.short_name === country
		) ?? false
	);
};

const getValidLatLng = (results: google.maps.GeocoderResult[] | null, country: string) => {
	const isSameCountry = isSameCountryAddress(results, country);

	if (!isSameCountry) {
		return undefined;
	}

	return getLatLng(results);
};

export const validateGeolocationAddress = async (address: EditableAddress) => {
	const exteriorNumber = isNoExteriorNumber(address.addressLine2)
		? undefined
		: address.addressLine2;
	const street = [address.addressLine1, exteriorNumber]
		.map((part) => part?.trim())
		.filter(Boolean)
		.join(' ');
	const colony = address.addressLine3 === OTHER_COLONY ? undefined : address.addressLine3;
	const addressCleaned = [street, colony, address.city, address.state]
		.map((part) => part?.trim())
		.filter(Boolean)
		.join(', ');
	const { results, status } = await geocodeByZipCode(
		address.zipCode,
		addressCleaned,
		address.country
	);

	const latLng = getValidLatLng(results, address.country);
	if (latLng) return latLng;

	// Second attempt if the first attempt did not find results
	if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
		const gpsAddress = [address.state, address.city, address.addressLine3, address.zipCode]
			.filter(Boolean)
			.join(', ');
		const { results } = await geocodeByZipCode(undefined, gpsAddress, address.country);
		const latLng = getValidLatLng(results, address.country);
		if (latLng) return latLng;
	}

	// No geolocation data found
	return undefined;
};

export const onBeforeInputNumeric = (
	e: React.FormEvent<HTMLInputElement> & { nativeEvent?: InputEvent }
) => {
	const data: string | null = e.nativeEvent?.data ?? null;
	if (data && /[^\d]/.test(data)) {
		e.preventDefault();
	}
};

export const onPasteNumeric = (e: React.ClipboardEvent<HTMLInputElement>) => {
	const text = e.clipboardData.getData('text');
	if (/[^\d]/.test(text)) {
		e.preventDefault();
		const onlyDigits = text.replace(/\D/g, '');
		const target = e.target as HTMLInputElement;
		const start = target.selectionStart ?? target.value.length;
		const end = target.selectionEnd ?? target.value.length;
		const next = target.value.slice(0, start) + onlyDigits + target.value.slice(end);
		target.value = next;
		target.dispatchEvent(new Event('input', { bubbles: true }));
	}
};

export const onDropNumeric = (e: React.DragEvent<HTMLInputElement>) => {
	const text = e.dataTransfer.getData('text');
	if (/[^\d]/.test(text)) {
		e.preventDefault();
		(e.target as HTMLInputElement).focus();
		document.execCommand('insertText', false, text.replace(/\D/g, ''));
	}
};

export const buildAddressPayload = (address: EditableAddress) => {
	const {
		addressLine1,
		addressLine2,
		addressLine3,
		addressField1,
		addressField3,
		addressLine3Txt: _addressLine3Txt,
		nickName,
		addressId,
		...rest
	} = address;
	return {
		nickName,
		isUpdate: !!addressId,
		data: {
			addressLine: [addressLine1, addressLine2 ?? '', addressLine3 ?? ''],
			field3: addressField3 ?? '',
			xaddr_addressField1: addressField1,
			xaddr_addressField3: addressField3 ?? '',
			...rest,
		},
	};
};
